// ─────────────────────────────────────────────────────────────────────────
// phoneDisplay — a contact number as the RECORD stores it, not as the tenant's
// default happens to be set.
//
// The detail view formats every phone through one admin-configured mask
// (detailDefaults.phoneFormat, e.g. "3-3-4"). That part is right and stays.
// What was wrong is the country code: it came from a single global default, so
// a candidate who stored +91 was displayed as "+1 999-878-3413" — a number that
// does not exist, printed with total confidence.
//
// Resolution order, most specific first:
//   1. a code embedded in the value itself ("+91 9998783413") — unambiguous
//   2. `renderOptions.countryCodeField` — the Detail Groups admin naming the
//      sibling key that holds this record's code
//   3. the conventional siblings (<field>CountryCode, countryCode,
//      phoneCountryCode, mobileCountryCode, …)
//   4. the global default — ONLY when the record carries nothing, and it ships
//      empty so a missing code renders as no code instead of a wrong one.
//
// Nothing here knows a module or a field name: (2) is config and (3) is a
// naming convention applied to whatever key the field itself has.
// ─────────────────────────────────────────────────────────────────────────

import { formatPhone, getPhoneCountryCode } from '../../services/detailDefaults';

const isEmpty = (v) => v === undefined || v === null || v === '';

// getPath — dotted-path read, so a configured countryCodeField may point at a
// nested container key ("contact.countryCode") as well as a flat one.
function getPath(source, path) {
  return String(path ?? '')
    .split('.')
    .filter(Boolean)
    .reduce((current, key) => (current == null ? undefined : current[key]), source);
}

/**
 * normalizeDialCode — "+91" | "91" | 91 → "+91". Anything that is not a dial
 * code (a country NAME, an empty string, a stray label) resolves to '' so it is
 * never printed in front of a number.
 */
export function normalizeDialCode(value) {
  const raw = String(value ?? '').trim().replace(/[\s()-]/g, '');
  if (!raw) return '';
  const digits = raw.replace(/^\+/, '');
  return /^\d{1,4}$/.test(digits) ? `+${digits}` : '';
}

/**
 * countryCodeKeys — the sibling keys to probe for a phone field's own code.
 * The field-derived candidates come first ("mobileNumberCountryCode",
 * "mobileCountryCode" for a `mobileNumber` field) so a record holding several
 * numbers keeps each one's code attached to the right number.
 */
export function countryCodeKeys(field) {
  const key = String(field?.field ?? '').trim();
  const base = key.replace(/(number|no|phone|mobile|contact)$/i, '');
  return [
    ...(key ? [`${key}CountryCode`, `${key}Code`] : []),
    ...(base && base !== key ? [`${base}CountryCode`] : []),
    'countryCode',
    'phoneCountryCode',
    'mobileCountryCode',
    'contactCountryCode',
    'dialCode',
    'isdCode',
  ];
}

/**
 * resolveCountryCode — this record's dial code for this field.
 * `record` is the flattened value map of the record being displayed; pass the
 * addRow ROW when formatting a repeatable row, so a row's own code wins.
 */
export function resolveCountryCode(field, record, fallback = '') {
  const configured = field?.renderOptions?.countryCodeField ?? field?.countryCodeField;
  const keys = configured ? [configured, ...countryCodeKeys(field)] : countryCodeKeys(field);
  for (const key of keys) {
    const code = normalizeDialCode(getPath(record, key));
    if (code) return code;
  }
  return normalizeDialCode(fallback);
}

/**
 * splitDialCode — separate a code the value already carries from the number.
 */
export function splitDialCode(raw) {
  const str = String(raw ?? '').trim();
  const match = str.match(/^(\+\d{1,4})[\s-]*(.*)$/);
  return match ? { code: match[1], rest: match[2] } : { code: '', rest: str };
}

/**
 * displayPhone — the finished, maskable display string.
 * `countryCode` is whatever resolveCountryCode produced; a code embedded in the
 * value still wins, because that is the record speaking for itself.
 */
export function displayPhone(raw, countryCode = '') {
  if (isEmpty(raw)) return '';
  const { code, rest } = splitDialCode(raw);
  const cc = code || normalizeDialCode(countryCode);
  const formatted = formatPhone(rest) || rest;
  return `${cc ? `${cc} ` : ''}${formatted}`.trim();
}

/**
 * displayPhoneForField — the one call sites use: resolve the code off the
 * record, then format. Falls back to the global default only as a last resort.
 */
export function displayPhoneForField(field, value, record) {
  const code = resolveCountryCode(field, record, getPhoneCountryCode());
  return displayPhone(value, code);
}
